import { createContext, useContext, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { api } from "../lib/api";
import { BUSINESS, SERVICE_SELECT_OPTIONS } from "../content/defaults";

const Ctx = createContext(null);
export const useRequestDialog = () => useContext(Ctx);

const EMPTY = {
  first_name: "", last_name: "", phone: "", email: "", make: "", model: "", year: "", mileage: "", vin: "",
  service: "Electrical diagnosis", concern: "", problem_started: "", frequency: "", starting_behavior: "",
  transport: "", warnings: "", triggers: "", tests: "", accessories: "", preferred_contact: "Email",
  history: "", date: "", preferred_time: "", consent: false,
};

export function RequestDialogProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [appointment, setAppointment] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [keyState, setKeyState] = useState(() => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random())));

  const openDialog = (service, concern = "", isAppointment = false, prefill = {}) => {
    setForm((f) => ({ ...EMPTY, ...prefill, service: service || f.service, concern: concern || prefill.concern || f.concern }));
    setAppointment(isAppointment);
    setResult(null);
    setError("");
    setOpen(true);
  };

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    if (result) { setResult(null); setKeyState(crypto.randomUUID()); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await api("/requests", { method: "POST", body: { data: form, submission_key: keyState } });
      setResult(res.request_number);
      setKeyState(crypto.randomUUID());
      setForm(EMPTY);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const F = (k, label, props = {}) => (
    <div className="field" key={k}>
      <label>{label}</label>
      {props.type === "textarea" ? (
        <textarea name={k} value={form[k]} onChange={set(k)} rows={props.rows || 3} placeholder={props.placeholder} required={props.required} />
      ) : props.options ? (
        <select name={k} value={form[k]} onChange={set(k)} required={props.required}>
          {props.options.map((o) => <option key={o} value={o === "Choose" ? "" : o}>{o}</option>)}
        </select>
      ) : (
        <input name={k} value={form[k]} onChange={set(k)} type={props.type || "text"} placeholder={props.placeholder}
          required={props.required} maxLength={props.maxLength} min={props.min} max={props.max} inputMode={props.inputMode} />
      )}
    </div>
  );

  return (
    <Ctx.Provider value={openDialog}>
      {children}
      {open && <DialogBody appointment={appointment} onClose={() => setOpen(false)}>{{
        F, form, busy, error, result, submit, set,
      }}</DialogBody>}
    </Ctx.Provider>
  );
}

function DialogBody({ appointment, onClose, children }) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.showModal();
  }, []);
  const { F, form, busy, error, result, submit, set } = children;
  return (
    <dialog className="ccc-dialog" ref={ref} onClose={onClose} aria-labelledby="ccc-intake-title" data-testid="request-dialog">
      <div className="dialog-head">
        <h2 id="ccc-intake-title">{appointment ? "Request an appointment" : "Describe your repair"}</h2>
        <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close repair form" data-testid="request-dialog-close"><X size={15} /> Close</button>
      </div>
      <div className="dialog-body">
        <p className="form-note" style={{ marginTop: 0 }}>
          Share your vehicle and symptoms. The shop receives your request and must confirm availability.
          Preferred dates are requests, not bookings.
        </p>
        <form onSubmit={submit} data-testid="request-form">
              <div className="field-pair">
                {F("first_name", "First name", { required: true, placeholder: "Maria" })}
                {F("last_name", "Last name", { required: true, placeholder: "Lopez" })}
              </div>
              <div className="field-pair">
                {F("phone", "Phone", { required: true, type: "tel", placeholder: "(562) 555-0100" })}
                {F("email", "Email", { required: true, type: "email", placeholder: "you@example.com" })}
              </div>
              <div className="field-pair">
                {F("make", "Vehicle make", { required: true, placeholder: "Jeep, Toyota, Ford…" })}
                {F("model", "Vehicle model", { required: true, placeholder: "Grand Cherokee, Camry…" })}
              </div>
              <div className="field-pair">
                {F("year", "Vehicle year", { required: true, type: "number", min: 1900, max: new Date().getFullYear() + 2, placeholder: "2017", inputMode: "numeric" })}
                {F("mileage", "Mileage (optional)", { type: "number", min: 0, inputMode: "numeric" })}
              </div>
              {F("vin", "VIN (optional)", { maxLength: 17, placeholder: "Vehicle identification number" })}
              <h3 style={{ fontSize: 15, margin: "20px 0 12px", color: "var(--blue-bright)" }}>Service &amp; diagnostic details</h3>
              {F("service", "Service requested", { required: true, options: SERVICE_SELECT_OPTIONS })}
              {F("concern", "What is happening?", { required: true, type: "textarea", rows: 4, placeholder: "When does it happen? What sounds or warning messages do you notice?" })}
              {F("problem_started", "When did the problem start?", { required: true, placeholder: "Yesterday, last month, after a repair…" })}
              {F("frequency", "How often does it happen?", { required: true, options: ["Choose", "Every time", "Sometimes / intermittent", "After sitting", "Only while driving", "Not sure"] })}
              {F("starting_behavior", "Does the engine start?", { required: true, options: ["Choose", "Starts normally", "No crank / no response", "Clicks but does not crank", "Cranks slowly", "Cranks but does not start", "Starts only with a jump", "Not sure"] })}
              {F("transport", "Vehicle transport", { required: true, options: ["Choose", "Vehicle can be brought in", "Vehicle does not run / transport needed", "Unsure — please contact me"] })}
              {F("warnings", "Warning lights, messages or sounds (optional)", { type: "textarea", rows: 2, placeholder: "Describe exact messages, clicks, noises or flickering lights" })}
              {F("triggers", "What makes the problem appear? (optional)", { type: "textarea", rows: 2, placeholder: "Rain, heat, bumps, cold starts, overnight parking, accessories…" })}
              {F("tests", "Parts already replaced or tests performed (optional)", { type: "textarea", rows: 2, placeholder: "Battery, starter, alternator, fuses, test reports…" })}
              {F("accessories", "Aftermarket accessories or recent changes (optional)", { type: "textarea", rows: 2, placeholder: "Alarm, stereo, camera, chargers or wiring work" })}
              {F("preferred_contact", "Preferred contact", { options: ["Email", "Phone"] })}
              {F("history", "Recent repairs or tests (optional)", { type: "textarea", rows: 2 })}
              <div className="field-pair">
                {F("date", "Preferred date (optional)", { type: "date" })}
                {F("preferred_time", "Preferred time window (optional)", { options: ["Shop will contact me", "Morning", "Afternoon"] })}
              </div>
              <label className="consent">
                <input type="checkbox" checked={form.consent} onChange={set("consent")} required data-testid="request-consent-checkbox" />
                <span>I authorize {BUSINESS.name} to contact me about this repair request.</span>
              </label>
              {error && <div className="error-box" role="alert" data-testid="request-error">{error}</div>}
              {result ? (
                <div className="ok-box" role="status" data-testid="request-success">
                  <strong>{result} received.</strong> The shop must confirm your appointment. Call {BUSINESS.phoneDisplay} with questions.
                </div>
              ) : (
                <button className="btn btn-red" type="submit" disabled={busy} data-testid="request-submit-button" style={{ width: "100%" }}>
                  {busy ? "Sending…" : "Send Repair Request"}
                </button>
              )}
            </form>
          </div>
        </dialog>
  );
}
